// =============================================================================
// FuelCardPanel — fuel per vehicle from the fuel card report (mig 405, 29 Sep 2026)
// =============================================================================
// James: "a simple drop file in vehicles for fuel report and the worker puts
// in the details we can easily track the data and see say who's using the
// most fuel." Drop the Shell/Viva transaction report → uploaded to
// ap-documents/fuel-reports → /vehicles/fuel-extract reads it → the rows land
// on the vehicle whose rego heads each card block → this panel draws litres,
// fills and dollars per ute, month by month.
//
// Not AP. The Viva bill still comes through AP as one lump on 6200; these rows
// are the detail behind it. The same report dropped twice adds nothing (the
// fill's card + date + docket + amount is the key, checked before insert).
// =============================================================================
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, Legend } from "recharts";
import { Fuel, Upload, CheckCircle2, AlertTriangle, FileText, X } from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Button, Spinner, Pill } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

const VEHICLE_COLOURS = ["#F97316", "#3B82F6", "#10B981", "#A855F7", "#EAB308", "#EC4899", "#14B8A6", "#EF4444", "#84CC16", "#0EA5E9"];
const UNKNOWN = "__unknown__";

function monthKey(d: Date) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; }
function monthLabel(key: string) { const [y, m] = key.split("-"); return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(m) - 1]} ${y.slice(2)}`; }
function fyEnding(now = new Date()) { return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear(); }
function fyLabel(e: number) { return `FY${String(e - 1).slice(2)}/${String(e).slice(2)}`; }
function monthsIntoFy(now = new Date()) { const s = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1; return (now.getFullYear() - s) * 12 + (now.getMonth() - 6) + 1; }

type Txn = { rego: string; card_no: string | null; sale_date: string; doc_no: string | null; location: string | null; product: string | null;
             litres: number; price_per_l: number | null; amount_ex_gst: number | null; gst: number | null; amount_inc_gst: number; odometer: number | null };
type Extract = { confidence: number; header: { supplier: string | null; account_no: string | null; statement_no: string | null; statement_date: string | null; period_from: string | null; period_to: string | null; total_litres: number | null; total_amount_inc: number | null }; transactions: Txn[]; reconciles: boolean | null };

// James, 29 Sep: "then there is the fuel that comes in through AP in fuel
// station receipts for the vehicles, and sometimes Clint on his diesel but
// that's not an allocated asset, so a bucket of fuel not on allocated cars …
// sometimes we get fuel for machines." So the panel also takes the fuel bills
// from AP (6200 / OX12_FUEL rows of v_vehicle_spend): a receipt with a vehicle
// picked adds to that vehicle's dollars; one without lands in the open bucket.
export default function FuelCardPanel({ vehicles, fuelBills = [] }: { vehicles: any[]; fuelBills?: any[] }) {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const today = new Date();
  const thisFy = fyEnding(today);
  const from12 = monthKey(new Date(today.getFullYear(), today.getMonth() - 11, 1));

  const { data: months = [], isLoading } = useQuery({
    queryKey: ["vehicle_fuel_month"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("v_vehicle_fuel_month").select("*").order("month");
      if (error) throw error; return (data ?? []) as any[];
    },
  });
  const { data: imports = [] } = useQuery({
    queryKey: ["fuel_card_imports"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("fuel_card_imports").select("*").order("imported_at", { ascending: false }).limit(12);
      if (error) throw error; return (data ?? []) as any[];
    },
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["vehicle_fuel_month"] }); qc.invalidateQueries({ queryKey: ["fuel_card_imports"] }); };

  // James, 29 Sep: "want to make sure the Viva invoice that is seen in a bill
  // is not being double charged, as it's in the transaction report." Right —
  // the monthly card statement bill IS the fills, already counted above. So a
  // bill from the card issuer (Viva Energy, or whoever an imported report
  // says) is set aside: not in the receipts column, not in the chart, shown
  // once as a reconciliation line against the card fills instead.
  const issuerNames = useMemo(() => {
    const names = new Set<string>(["viva energy"]);
    for (const im of imports) if (im.supplier) names.add(String(im.supplier).toLowerCase());
    return [...names];
  }, [imports]);
  const isStatementBill = (b: any) => { const sup = String(b.supplier_name ?? "").toLowerCase(); return b.fuel_litres == null && issuerNames.some(n => sup.includes(n.split(" ")[0])); };
  const statementBills = useMemo(() => fuelBills.filter(isStatementBill), [fuelBills, issuerNames]);   // eslint-disable-line react-hooks/exhaustive-deps
  const receiptBills = useMemo(() => fuelBills.filter(b => !isStatementBill(b)), [fuelBills, issuerNames]);   // eslint-disable-line react-hooks/exhaustive-deps

  const regoNorm = (r: string) => String(r ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const vehicleByRego = useMemo(() => { const m = new Map<string, any>(); for (const v of vehicles) if (v.rego) m.set(regoNorm(v.rego), v); return m; }, [vehicles]);
  const vehicleLabel = (v: any) => [v.vehicle_code, v.rego].filter(Boolean).join(" · ");

  // -- per vehicle, this FY -------------------------------------------------------
  const OPEN = "__open__";
  const perVehicle = useMemo(() => {
    const m = new Map<string, { key: string; label: string; fills: number; litres: number; inc: number; receipts: number; receiptLitres: number; lastFill: string | null; regos: Set<string> }>();
    for (const r of months) {
      if (r.fy_ending !== thisFy) continue;
      const key = r.vehicle_id ?? UNKNOWN;
      const v = r.vehicle_id ? vehicles.find(x => x.vehicle_id === r.vehicle_id) : null;
      const hit = m.get(key) ?? m.set(key, { key, label: v ? vehicleLabel(v) : (r.vehicle_id ? [r.vehicle_code, r.rego].filter(Boolean).join(" · ") : "Rego not on the register"), fills: 0, litres: 0, inc: 0, receipts: 0, receiptLitres: 0, lastFill: null, regos: new Set() }).get(key)!;
      hit.fills += Number(r.fills); hit.litres += Number(r.litres); hit.inc += Number(r.amount_inc_gst);
      if (!hit.lastFill || r.last_fill > hit.lastFill) hit.lastFill = r.last_fill;
      hit.regos.add(r.rego);
    }
    // Fuel bought on a servo receipt through AP (this FY): on the vehicle if one
    // was picked, else the open bucket — Clint's diesel, fuel for machines.
    const fyFrom = `${thisFy - 1}-07-01`;
    for (const b of receiptBills) {
      if (!b.spend_date || b.spend_date < fyFrom) continue;
      const key = b.vehicle_id ?? OPEN;
      const v = b.vehicle_id ? vehicles.find(x => x.vehicle_id === b.vehicle_id) : null;
      const hit = m.get(key) ?? m.set(key, { key, label: v ? vehicleLabel(v) : (b.vehicle_id ? [b.vehicle_code, b.rego].filter(Boolean).join(" · ") : "Fuel not on a vehicle — receipts, Clint's diesel, machines"), fills: 0, litres: 0, inc: 0, receipts: 0, receiptLitres: 0, lastFill: null, regos: new Set() }).get(key)!;
      hit.receipts += Number(b.amount_inc_gst ?? 0);
      // mig 406 — litres read off the receipt's description (or typed on the coding card)
      if (b.fuel_litres != null) { hit.receiptLitres += Number(b.fuel_litres); hit.fills += 1; if (!hit.lastFill || b.spend_date > hit.lastFill) hit.lastFill = b.spend_date; }
    }
    const mo = monthsIntoFy(today);
    const rank = (k: string) => (k === OPEN ? 2 : k === UNKNOWN ? 1 : 0);
    return [...m.values()].map(x => { const litres = x.litres + x.receiptLitres; const dollars = x.inc + x.receipts; return { ...x, litres, perMonth: litres / mo, avg: litres > 0 ? dollars / litres : 0 }; })
      .sort((a, b) => rank(a.key) - rank(b.key) || b.litres - a.litres || b.receipts - a.receipts);
  }, [months, vehicles, receiptBills, thisFy, today]);
  const unknownRegos = useMemo(() => [...new Set(months.filter(r => !r.vehicle_id).map(r => r.rego))], [months]);

  // -- month by month, last 12, stacked by vehicle ---------------------------------
  // James, 29 Sep: "I reconciled the receipts to the Prado 670ZLE as it's all
  // unleaded and not on the fuel card, but it's not showing up in the fuel
  // graphs." Receipts carry dollars, not litres — so the month chart is in
  // DOLLARS (card fills + servo receipts) and every vehicle is on it, with
  // the litres in the table above for the card-fed ones.
  const OPEN_KEY = "__open__";
  const series = useMemo(() => {
    const keys: string[] = []; const label = new Map<string, string>();
    const add = (k: string, name: string) => { if (!keys.includes(k)) { keys.push(k); label.set(k, name); } };
    const nameOf = (vid: string | null, rego: string | null) => { const v = vid ? vehicles.find(x => x.vehicle_id === vid) : null; return v ? (v.rego || v.vehicle_code) : (rego ?? "unknown"); };
    const ms: string[] = []; for (let i = 11; i >= 0; i--) ms.push(monthKey(new Date(today.getFullYear(), today.getMonth() - i, 1)));
    const rows = ms.map(k => ({ month: k, label: monthLabel(k) } as any));
    const idx = new Map(rows.map(r => [r.month, r]));
    for (const r of months) { const k = r.vehicle_id ?? UNKNOWN; add(k, nameOf(r.vehicle_id, r.rego)); const hit = idx.get(r.month); if (hit) hit[k] = (hit[k] ?? 0) + Number(r.amount_inc_gst); }
    for (const b of receiptBills) {
      if (!b.spend_month) continue;
      const k = b.vehicle_id ?? OPEN_KEY; add(k, b.vehicle_id ? nameOf(b.vehicle_id, b.rego) : "not on a vehicle");
      const hit = idx.get(b.spend_month); if (hit) hit[k] = (hit[k] ?? 0) + Number(b.amount_inc_gst ?? 0);
    }
    for (const row of rows) for (const k of keys) row[k] = row[k] ?? 0;
    return { keys, label, rows };
  }, [months, receiptBills, vehicles, today]);

  // -- the drop zone ----------------------------------------------------------------
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<"idle" | "uploading" | "reading" | "review" | "importing" | "done">("idle");
  const [err, setErr] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [storagePath, setStoragePath] = useState<string>("");
  const [extract, setExtract] = useState<Extract | null>(null);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);

  async function handleFile(f: File) {
    setErr(null); setResult(null); setExtract(null); setFileName(f.name);
    try {
      setStage("uploading");
      const ym = new Date().toISOString().slice(0, 7);
      const safe = f.name.replace(/[^A-Za-z0-9._-]+/g, "_");
      const path = `fuel-reports/${ym}/${Date.now()}_${safe}`;
      const { error: upErr } = await supabase.storage.from("ap-documents").upload(path, f, { contentType: f.type || "application/pdf", upsert: false });
      if (upErr) throw new Error(`Upload failed: ${upErr.message}`);
      setStoragePath(path);
      setStage("reading");
      const res = await apiFetch("/vehicles/fuel-extract", { method: "POST", body: JSON.stringify({ storage_path: path }) });
      if (!res.ok) { const t = await res.text().catch(() => ""); throw new Error(`Couldn't read the report (${res.status}): ${t.slice(0, 300)}`); }
      const x = await res.json() as Extract;
      if (!x.transactions?.length) throw new Error("No fuel lines found on that file — is it the transaction report (not the tax invoice)?");
      setExtract(x); setStage("review");
    } catch (e: any) { setErr(e?.message ?? String(e)); setStage("idle"); }
  }

  async function commit() {
    if (!extract) return;
    setStage("importing"); setErr(null);
    try {
      const tx = extract.transactions;
      const dates = tx.map(t => t.sale_date).sort();
      // already-there check on the natural key (card + date + doc + amount)
      const { data: existing, error: exErr } = await (supabase as any).from("fuel_card_transactions")
        .select("card_no, sale_date, doc_no, amount_inc_gst").gte("sale_date", dates[0]).lte("sale_date", dates[dates.length - 1]);
      if (exErr) throw exErr;
      const seen = new Set((existing ?? []).map((e: any) => `${e.card_no ?? ""}|${e.sale_date}|${e.doc_no ?? ""}|${Number(e.amount_inc_gst).toFixed(2)}`));
      const fresh = tx.filter(t => !seen.has(`${t.card_no ?? ""}|${t.sale_date}|${t.doc_no ?? ""}|${Number(t.amount_inc_gst).toFixed(2)}`));
      const { data: imp, error: impErr } = await (supabase as any).from("fuel_card_imports").insert({
        supplier: extract.header.supplier ?? "Viva Energy", account_no: extract.header.account_no, statement_no: extract.header.statement_no,
        statement_date: extract.header.statement_date, period_from: extract.header.period_from ?? dates[0], period_to: extract.header.period_to ?? dates[dates.length - 1],
        file_name: fileName, storage_path: storagePath, rows_imported: fresh.length, rows_skipped: tx.length - fresh.length,
        litres: fresh.reduce((a, t) => a + t.litres, 0), amount_inc_gst: fresh.reduce((a, t) => a + t.amount_inc_gst, 0),
        imported_by: user?.user_id ?? null,
      }).select("import_id").single();
      if (impErr) throw impErr;
      if (fresh.length) {
        const { error: insErr } = await (supabase as any).from("fuel_card_transactions").insert(fresh.map(t => ({ ...t, import_id: imp.import_id })));
        if (insErr) throw insErr;
      }
      setResult({ imported: fresh.length, skipped: tx.length - fresh.length });
      setStage("done"); refresh();
    } catch (e: any) { setErr(e?.message ?? String(e)); setStage("review"); }
  }

  async function assignRego(rego: string, vehicleId: string) {
    const { error } = await (supabase as any).from("fuel_card_transactions").update({ vehicle_id: vehicleId || null }).eq("rego", rego).is("vehicle_id", null);
    if (error) { window.alert(error.message); return; }
    refresh();
  }

  const money0 = (n: number) => "$" + Math.round(n).toLocaleString("en-AU");
  const l0 = (n: number) => Math.round(n).toLocaleString("en-AU") + " L";

  return (
    <Card className="mb-4">
      <div className="p-4 border-b border-line flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-semibold text-[14px] flex items-center gap-2"><Fuel size={15} className="text-accent" /> Fuel card — litres per vehicle</div>
          <div className="text-[11px] text-text-muted mt-0.5">Card fills from the Shell / Viva transaction report (each lands on the rego that heads its card) plus servo receipts through AP with their litres read off the description. Drop each report as it arrives; the same one twice adds nothing.</div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.currentTarget.value = ""; }} />
          <Button size="sm" icon={Upload} onClick={() => fileRef.current?.click()} disabled={stage === "uploading" || stage === "reading" || stage === "importing"}>
            {stage === "uploading" ? "Uploading…" : stage === "reading" ? "Reading the report…" : "Drop a transaction report"}
          </Button>
        </div>
      </div>

      {/* drop target */}
      <div
        onDragOver={e => { e.preventDefault(); }}
        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) void handleFile(f); }}
        className="mx-4 mt-3 rounded border border-dashed border-line px-3 py-2 text-[11px] text-text-dim text-center">
        …or drag the PDF here. Shell Card (Viva) TR005 reports read straight in; BP / Ampol should too — check the totals line before you import.
      </div>

      {err && <div className="mx-4 mt-3 bg-danger/10 border border-danger/30 rounded-md p-2.5 text-[12px] text-danger flex items-start gap-2"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {err}</div>}

      {/* review before import */}
      {extract && (stage === "review" || stage === "importing" || stage === "done") && (
        <div className="mx-4 mt-3 rounded border border-line bg-ink-raised/40">
          <div className="px-3 py-2 border-b border-line flex items-center justify-between gap-2 flex-wrap text-[12px]">
            <div className="flex items-center gap-2 flex-wrap">
              <FileText size={13} className="text-text-muted" />
              <span className="font-medium">{fileName}</span>
              <span className="text-text-muted">{extract.header.supplier ?? "—"} · statement {extract.header.statement_no ?? "—"} · {extract.header.statement_date ? fmtDate(extract.header.statement_date) : "—"}</span>
              <Pill label={`${extract.transactions.length} fills · ${l0(extract.transactions.reduce((a, t) => a + t.litres, 0))} · ${fmtMoney(extract.transactions.reduce((a, t) => a + t.amount_inc_gst, 0))} inc`} tone="default" small />
              {extract.reconciles === true && <Pill label="adds up to the report total" tone="approved" small />}
              {extract.reconciles === false && <Pill label={`does NOT add up — report says ${fmtMoney(extract.header.total_amount_inc ?? 0)}`} tone="rejected" small />}
              {extract.confidence < 0.7 && <Pill label={`low confidence ${Math.round(extract.confidence * 100)}%`} tone="pending" small />}
            </div>
            <div className="flex items-center gap-2">
              {stage === "done" && result && <span className="text-[12px] text-ok inline-flex items-center gap-1"><CheckCircle2 size={13} /> {result.imported} imported{result.skipped ? `, ${result.skipped} already there` : ""}</span>}
              {stage !== "done" && <Button size="sm" variant="primary" onClick={commit} disabled={stage === "importing"}>{stage === "importing" ? "Importing…" : "Import these fills"}</Button>}
              <button type="button" onClick={() => { setExtract(null); setStage("idle"); setResult(null); }} className="text-text-muted hover:text-text"><X size={14} /></button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-[12px]">
              <thead><tr className="border-b border-line">
                {["Rego", "Vehicle", "Date", "Doc", "Where", "Product", "Litres", "$/L", "Inc GST"].map(h => <th key={h} className="text-left px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>)}
              </tr></thead>
              <tbody>
                {extract.transactions.map((t, i) => {
                  const v = vehicleByRego.get(regoNorm(t.rego));
                  return (
                    <tr key={i} className={`border-b border-line last:border-b-0 ${i % 2 ? "bg-ink/40" : ""}`}>
                      <td className="px-3 py-1.5 font-mono">{t.rego}</td>
                      <td className="px-3 py-1.5">{v ? vehicleLabel(v) : <span className="text-warn">not on the register — lands unmatched, pick it below after import</span>}</td>
                      <td className="px-3 py-1.5 font-mono text-text-muted">{fmtDate(t.sale_date)}</td>
                      <td className="px-3 py-1.5 font-mono text-text-dim">{t.doc_no ?? "—"}</td>
                      <td className="px-3 py-1.5 truncate max-w-[220px]">{t.location ?? "—"}</td>
                      <td className="px-3 py-1.5 text-text-muted">{t.product ?? "—"}</td>
                      <td className="px-3 py-1.5 font-mono text-right">{t.litres.toFixed(2)}</td>
                      <td className="px-3 py-1.5 font-mono text-right text-text-muted">{t.price_per_l != null ? t.price_per_l.toFixed(4) : "—"}</td>
                      <td className="px-3 py-1.5 font-mono text-right">{fmtMoney(t.amount_inc_gst)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isLoading ? <div className="py-8 flex justify-center"><Spinner /></div> : months.length === 0 && fuelBills.length === 0 ? (
        <div className="px-4 py-8 text-center text-[12px] text-text-muted italic">No fuel card fills yet — drop the first transaction report above.</div>
      ) : (
        <>
          {/* per vehicle this FY */}
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-[13px] min-w-[760px]">
              <thead><tr className="border-b border-line">
                {["Vehicle", "Fills", "Litres", "Card $", "$/L", "L / month", "Receipts $", "Last fill"].map((h, i) => <th key={h} className={`px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted ${i === 0 ? "text-left" : "text-right"}`} title={h === "Receipts $" ? "Fuel bought on a servo receipt through AP (6200 / OX12_FUEL), inc GST" : h === "Card $" ? "Fuel card fills, inc GST" : h === "Litres" ? "Card fills plus litres read off servo receipts (mig 406)" : h === "$/L" ? "All fuel dollars ÷ all litres" : undefined}>{h} {i === 1 ? <span className="normal-case tracking-normal">· {fyLabel(thisFy)}</span> : null}</th>)}
              </tr></thead>
              <tbody>
                {perVehicle.map((v, i) => (
                  <tr key={v.key} className={`border-b border-line last:border-b-0 ${i % 2 ? "bg-ink/40" : ""} ${v.key === UNKNOWN || v.key === OPEN ? "text-text-muted" : ""}`}>
                    <td className="px-4 py-2">{v.key === UNKNOWN ? <span className="italic">{v.label} <span className="font-mono not-italic">({[...v.regos].join(", ")})</span></span> : v.key === OPEN ? <span className="italic">{v.label}</span> : v.label}</td>
                    <td className="px-4 py-2 font-mono text-right">{v.fills || <span className="text-text-dim">—</span>}</td>
                    <td className="px-4 py-2 font-mono text-right font-semibold">{v.litres ? l0(v.litres) : <span className="text-text-dim font-normal">—</span>}</td>
                    <td className="px-4 py-2 font-mono text-right">{v.inc ? money0(v.inc) : <span className="text-text-dim">—</span>}</td>
                    <td className="px-4 py-2 font-mono text-right text-text-muted">{v.avg ? v.avg.toFixed(2) : "—"}</td>
                    <td className="px-4 py-2 font-mono text-right text-text-muted">{v.litres ? l0(v.perMonth) : "—"}</td>
                    <td className="px-4 py-2 font-mono text-right text-text-muted">{v.receipts ? money0(v.receipts) : <span className="text-text-dim">—</span>}</td>
                    <td className="px-4 py-2 font-mono text-right text-text-muted">{v.lastFill ? fmtDate(v.lastFill) : "—"}</td>
                  </tr>
                ))}
                {perVehicle.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-[12px] text-text-muted italic">No fills this financial year yet.</td></tr>}
              </tbody>
            </table>
          </div>

          {/* the card statement vs the card fills — same money, shown once */}
          {(() => {
            const fyFrom = `${thisFy - 1}-07-01`;
            const stmt = statementBills.filter(b => b.spend_date >= fyFrom).reduce((a, b) => a + Number(b.amount_inc_gst ?? 0), 0);
            const cardInc = months.filter(r => r.fy_ending === thisFy).reduce((a, r) => a + Number(r.amount_inc_gst), 0);
            if (!stmt && !cardInc) return null;
            const gap = stmt - cardInc;
            return (
              <div className="mx-4 mt-3 rounded border border-line bg-ink-raised/40 px-3 py-2 text-[12px] flex items-center gap-3 flex-wrap">
                <span className="text-text-muted">Card statement bills in AP this FY:</span>
                <span className="font-mono">{fmtMoney(stmt)}</span>
                <span className="text-text-dim">·</span>
                <span className="text-text-muted">card fills imported:</span>
                <span className="font-mono">{fmtMoney(cardInc)}</span>
                <span className="text-text-dim">·</span>
                {Math.abs(gap) < 1
                  ? <span className="text-ok inline-flex items-center gap-1"><CheckCircle2 size={12} /> match</span>
                  : <span className={gap > 0 ? "text-warn" : "text-text-muted"}>{gap > 0 ? `${fmtMoney(gap)} of statement not yet imported as fills` : `${fmtMoney(-gap)} of fills not yet billed`}</span>}
                <span className="text-text-dim basis-full">The statement bill is the same money as the fills — it is counted once, in the card rows, never as a receipt.</span>
              </div>
            );
          })()}

          {unknownRegos.length > 0 && (
            <div className="mx-4 mt-3 rounded border border-warn/40 bg-warn/5 p-3 text-[12px]">
              <div className="font-medium text-warn mb-1.5">Regos on the card that aren't on the vehicle register</div>
              <div className="space-y-1.5">
                {unknownRegos.map(r => (
                  <div key={r} className="flex items-center gap-2">
                    <span className="font-mono w-20">{r}</span>
                    <select defaultValue="" onChange={e => { if (e.target.value) void assignRego(r, e.target.value); }}
                      className="bg-ink border border-line rounded px-2 py-1 text-[12px] text-text">
                      <option value="">— which vehicle is this? —</option>
                      {vehicles.map((v: any) => <option key={v.vehicle_id} value={v.vehicle_id}>{vehicleLabel(v)}</option>)}
                    </select>
                    <span className="text-text-dim">or add it to the register with this rego and it matches itself</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-4">
            <div className="text-[13px] font-medium">Fuel spend month by month</div>
            <div className="text-[11px] text-text-muted mb-2">Last 12 months, inc GST, stacked by vehicle — card fills plus servo receipts, so a vehicle fuelled on receipts (unleaded, no card) is here too. Litres are in the table above.</div>
            <div style={{ width: "100%", height: 220 }}><ResponsiveContainer>
              <BarChart data={series.rows} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff14" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v: any) => `$${Math.round(Number(v))}`} />
                <RTooltip formatter={(v: any, n: any) => [fmtMoney(Number(v)), series.label.get(String(n)) ?? String(n)]} contentStyle={{ background: "#111827", border: "1px solid #374151", borderRadius: 6, fontSize: 12 }} />
                <Legend formatter={(n: any) => series.label.get(String(n)) ?? String(n)} wrapperStyle={{ fontSize: 11 }} />
                {series.keys.map((k, i) => <Bar key={k} dataKey={k} stackId="fuel" fill={k === UNKNOWN || k === OPEN_KEY ? "#6B7280" : VEHICLE_COLOURS[i % VEHICLE_COLOURS.length]} />)}
              </BarChart>
            </ResponsiveContainer></div>
          </div>

          {imports.length > 0 && (
            <div className="px-4 pb-3 text-[11px] text-text-dim">
              Reports loaded: {imports.map((im: any) => `${im.statement_no ? `#${im.statement_no}` : im.file_name} (${im.rows_imported} fills${im.rows_skipped ? `, ${im.rows_skipped} dup` : ""})`).join(" · ")}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
